'use client';

import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { getCurrentVariant } from './abTesting';

/**
 * PostHog A/B Testing Hooks
 *
 * Story 6.6: A/B Variant Refinement & Admin Analytics
 *
 * Provides hooks for PostHog feature flag-based A/B testing:
 * - Page-level variants (A, B, C)
 * - Component-level variants (headline, CTA, subhead)
 *
 * Migration strategy:
 * - PostHog is primary for new visitors
 * - Falls back to existing cookie assignment for existing visitors
 * - After 30 days, cookie fallback can be removed
 */

// ============================================================================
// Type Definitions
// ============================================================================

export type PageVariant = 'A' | 'B' | 'C';
export type HeadlineVariant =
  | 'emotional'
  | 'speed'
  | 'pain'
  | 'aspirational'
  | 'social_proof';
export type CTAVariant =
  | 'action'
  | 'low_commitment'
  | 'speed_focused'
  | 'curiosity';
export type SubheadVariant = 'no_signup' | 'speed' | 'use_cases' | 'value';

// Feature flag keys (must match PostHog dashboard configuration)
export const FLAG_KEYS = {
  PAGE_VARIANT: 'landing-page-variant',
  HEADLINE: 'headline-variant',
  CTA: 'cta-variant',
  SUBHEAD: 'subhead-variant',
  INSPIRATION_FEED: 'inspiration-feed-enabled',
} as const;

// Default values for when PostHog is loading or unavailable
const DEFAULTS = {
  pageVariant: 'C' as PageVariant,
  headline: 'emotional' as HeadlineVariant,
  cta: 'action' as CTAVariant,
  subhead: 'no_signup' as SubheadVariant,
};

// ============================================================================
// Page Variant Hook (with cookie fallback)
// ============================================================================

export interface UsePageVariantReturn {
  variant: PageVariant;
  isLoading: boolean;
  source: 'posthog' | 'cookie' | 'default';
}

/**
 * Hook to get the landing page variant (A, B, or C)
 *
 * Priority:
 * 1. PostHog feature flag (if available and loaded)
 * 2. Existing cookie assignment (fallback for migration)
 * 3. Default value ('C')
 *
 * @example
 * ```tsx
 * const { variant, isLoading, source } = usePageVariant();
 *
 * if (isLoading) return <LoadingSkeleton />;
 *
 * switch (variant) {
 *   case 'A': return <VariantA />;
 *   case 'B': return <VariantB />;
 *   case 'C': return <VariantC />;
 * }
 * ```
 */
export function usePageVariant(): UsePageVariantReturn {
  const posthog = usePostHog();
  const posthogVariant = useFeatureFlagVariantKey(FLAG_KEYS.PAGE_VARIANT);
  const [result, setResult] = useState<UsePageVariantReturn>({
    variant: DEFAULTS.pageVariant,
    isLoading: true,
    source: 'default',
  });

  useEffect(() => {
    // Dev override: ?variant=A|B|C in URL forces a specific variant
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const override = params.get('variant');
      if (override) {
        const validated = validatePageVariant(override);
        setResult({ variant: validated, isLoading: false, source: 'default' });
        return;
      }
    }

    // Check if PostHog is ready and has the flag
    if (posthog && posthogVariant !== undefined && posthogVariant !== null) {
      // PostHog has loaded and returned a value
      const variant = validatePageVariant(posthogVariant);
      setResult({
        variant,
        isLoading: false,
        source: 'posthog',
      });
      return;
    }

    // PostHog not ready - check for cookie fallback
    const cookieAssignment = getCurrentVariant();
    if (cookieAssignment?.variant) {
      setResult({
        variant: cookieAssignment.variant as PageVariant,
        isLoading: false,
        source: 'cookie',
      });
      return;
    }

    // No PostHog, no cookie - use default (but still loading if PostHog might load)
    if (!posthog) {
      setResult({
        variant: DEFAULTS.pageVariant,
        isLoading: false,
        source: 'default',
      });
    }
  }, [posthog, posthogVariant]);

  return result;
}

/**
 * Validate and normalize page variant from PostHog
 */
function validatePageVariant(value: unknown): PageVariant {
  if (value === 'A' || value === 'B' || value === 'C') {
    return value;
  }
  // Map 'control' to default variant
  if (value === 'control') {
    return DEFAULTS.pageVariant;
  }
  // Map lowercase variants if needed
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'A' || upper === 'B' || upper === 'C') {
      return upper as PageVariant;
    }
  }
  return DEFAULTS.pageVariant;
}

// ============================================================================
// Component Variant Hooks (PostHog only, no cookie fallback)
// ============================================================================

export interface UseVariantReturn<T> {
  variant: T;
  isLoading: boolean;
}

/**
 * Hook to get the headline variant
 *
 * @example
 * ```tsx
 * const { variant, isLoading } = useHeadlineVariant();
 * const headline = HEADLINE_CONTENT[variant];
 * ```
 */
export function useHeadlineVariant(): UseVariantReturn<HeadlineVariant> {
  const variant = useFeatureFlagVariantKey(FLAG_KEYS.HEADLINE);

  // PostHog returns undefined while loading, null if flag doesn't exist
  const isLoading = variant === undefined;
  const validatedVariant = validateHeadlineVariant(variant);

  return {
    variant: validatedVariant,
    isLoading,
  };
}

function validateHeadlineVariant(value: unknown): HeadlineVariant {
  // Map 'control' to default variant
  if (value === 'control') return DEFAULTS.headline;
  const valid: HeadlineVariant[] = [
    'emotional',
    'speed',
    'pain',
    'aspirational',
    'social_proof',
  ];
  if (typeof value === 'string' && valid.includes(value as HeadlineVariant)) {
    return value as HeadlineVariant;
  }
  return DEFAULTS.headline;
}

/**
 * Hook to get the CTA button variant
 *
 * @example
 * ```tsx
 * const { variant, isLoading } = useCTAVariant();
 * const ctaText = CTA_CONTENT[variant];
 * ```
 */
export function useCTAVariant(): UseVariantReturn<CTAVariant> {
  const variant = useFeatureFlagVariantKey(FLAG_KEYS.CTA);

  const isLoading = variant === undefined;
  const validatedVariant = validateCTAVariant(variant);

  return {
    variant: validatedVariant,
    isLoading,
  };
}

function validateCTAVariant(value: unknown): CTAVariant {
  // Map 'control' to default variant
  if (value === 'control') return DEFAULTS.cta;
  const valid: CTAVariant[] = [
    'action',
    'low_commitment',
    'speed_focused',
    'curiosity',
  ];
  if (typeof value === 'string' && valid.includes(value as CTAVariant)) {
    return value as CTAVariant;
  }
  return DEFAULTS.cta;
}

/**
 * Hook to get the subhead variant
 *
 * @example
 * ```tsx
 * const { variant, isLoading } = useSubheadVariant();
 * const subhead = SUBHEAD_CONTENT[variant];
 * ```
 */
export function useSubheadVariant(): UseVariantReturn<SubheadVariant> {
  const variant = useFeatureFlagVariantKey(FLAG_KEYS.SUBHEAD);

  const isLoading = variant === undefined;
  const validatedVariant = validateSubheadVariant(variant);

  return {
    variant: validatedVariant,
    isLoading,
  };
}

function validateSubheadVariant(value: unknown): SubheadVariant {
  // Map 'control' to default variant
  if (value === 'control') return DEFAULTS.subhead;
  const valid: SubheadVariant[] = ['no_signup', 'speed', 'use_cases', 'value'];
  if (typeof value === 'string' && valid.includes(value as SubheadVariant)) {
    return value as SubheadVariant;
  }
  return DEFAULTS.subhead;
}

// ============================================================================
// Combined Hook for All Variants
// ============================================================================

export interface AllVariants {
  pageVariant: PageVariant;
  headlineVariant: HeadlineVariant;
  ctaVariant: CTAVariant;
  subheadVariant: SubheadVariant;
  isLoading: boolean;
  source: 'posthog' | 'cookie' | 'default';
}

/**
 * Hook to get all variant assignments at once
 * Useful for tracking and analytics
 *
 * @example
 * ```tsx
 * const variants = useAllVariants();
 *
 * useEffect(() => {
 *   if (!variants.isLoading) {
 *     trackLandingPageView(variants);
 *   }
 * }, [variants]);
 * ```
 */
export function useAllVariants(): AllVariants {
  const page = usePageVariant();
  const headline = useHeadlineVariant();
  const cta = useCTAVariant();
  const subhead = useSubheadVariant();

  return {
    pageVariant: page.variant,
    headlineVariant: headline.variant,
    ctaVariant: cta.variant,
    subheadVariant: subhead.variant,
    isLoading: page.isLoading || headline.isLoading || cta.isLoading || subhead.isLoading,
    source: page.source,
  };
}

// ============================================================================
// Inspiration Feed Hook (Story 6.13)
// ============================================================================

export interface UseInspirationFeedEnabledReturn {
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Hook to check if the inspiration feed is enabled via PostHog feature flag.
 *
 * Story 6.13: Bot Inspiration Feed on Landing Page
 *
 * - Story 19.52: GA — returns `enabled: true` by default (PostHog unavailable or
 *   flag unset); only an explicit "control" cohort hides it
 * - Returns `isLoading: true` while PostHog initializes (render nothing during loading)
 *
 * @example
 * ```tsx
 * const { enabled, isLoading } = useInspirationFeedEnabled();
 * if (isLoading || !enabled) return null;
 * return <BotInspirationFeed variant="vertical" />;
 * ```
 */
export function useInspirationFeedEnabled(): UseInspirationFeedEnabledReturn {
  const posthog = usePostHog();
  const variantKey = useFeatureFlagVariantKey(FLAG_KEYS.INSPIRATION_FEED);
  const [result, setResult] = useState<UseInspirationFeedEnabledReturn>({
    enabled: false,
    isLoading: true,
  });

  useEffect(() => {
    // Dev override: ?feed=1 in URL forces feed visible for local testing
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('feed') === '1') {
        setResult({ enabled: true, isLoading: false });
        return;
      }
    }

    // PostHog assigned an explicit cohort (multivariate: "control" | "test").
    // Story 19.52: the inspiration feed is GA — show it for everyone EXCEPT
    // users explicitly placed in a "control" cohort (so it can still be
    // A/B-measured later by assigning control).
    if (posthog && variantKey !== undefined) {
      setResult({
        enabled: variantKey !== 'control',
        isLoading: false,
      });
      return;
    }

    // PostHog unavailable OR the flag is not configured — default to VISIBLE
    // so the starting-ideas feed appears on all entry points (Story 19.52).
    setResult({
      enabled: true,
      isLoading: false,
    });
  }, [posthog, variantKey]);

  return result;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get variant values as flat object for event properties
 * Useful for analytics tracking
 */
export function getVariantProperties(variants: AllVariants): Record<string, string> {
  return {
    page_variant: variants.pageVariant,
    headline_variant: variants.headlineVariant,
    cta_variant: variants.ctaVariant,
    subhead_variant: variants.subheadVariant,
    variant_source: variants.source,
  };
}
